import React, { useState, useEffect, useCallback, useRef } from 'react';
import HomePage from './components/HomePage';
import ReviewPage from './components/ReviewPage';
import CarouselComparison from './components/CarouselComparison';
import DetailedItineraryPage from './components/DetailedItineraryPage';
import TrekDetailPage from './components/TrekDetailPage';
import DestinationPage from './components/DestinationPageNew';
import CuratedExperiences from './components/CuratedExperiences';
import DestinationsTilesPage from './components/DestinationsTilesPage';
import ContactPage from './components/ContactPage';
import AboutPage from './components/AboutPage';
import FlipCardTestPage from './components/FlipCardTestPage';
import ChatWidget from './components/ChatWidget';
import { SessionProvider } from './contexts/SessionContext';
import './App.css';

// ── URL ↔ Page helpers for deep linking ────────────────────────────────────
const VALID_PAGES = ['home','destinations','reviews','destination-page','curated-experiences','destinations-tiles','detailed-itinerary','trek-detail','about','contact','carousel-demo','flipcard-test'];

function parseUrlToPageData(): { page: string; data: any } {
  const path = window.location.pathname.replace(/^\//, '') || 'home';
  const params = new URLSearchParams(window.location.search);
  const page = VALID_PAGES.includes(path) ? path : 'home';
  let data: any = null;

  switch (page) {
    case 'destination-page':
    case 'curated-experiences': {
      const state = params.get('state');
      if (state) {
        data = { destination: { id: state, name: state.charAt(0).toUpperCase() + state.slice(1), state } };
      }
      const tourType = params.get('tourType');
      if (tourType && ['car', 'bike', 'trek'].includes(tourType)) {
        data = { ...data, tourType };
      }
      break;
    }
    case 'detailed-itinerary': {
      const id = params.get('id');
      const state = params.get('state') || 'ladakh';
      data = { itineraryId: id || '', state, fromCuratedExperiences: true };
      break;
    }
    case 'trek-detail': {
      const id = params.get('id');
      const state = params.get('state');
      data = { itineraryId: id || '', state };
      break;
    }
  }
  return { page, data };
}

function buildUrlFromPageData(page: string, data?: any): string {
  if (page === 'home') return '/';
  const params = new URLSearchParams();

  switch (page) {
    case 'destination-page':
    case 'curated-experiences': {
      const state = data?.destination?.state;
      if (state) params.set('state', state);
      if (data?.tourType) params.set('tourType', data.tourType);
      break;
    }
    case 'detailed-itinerary': {
      if (data?.itineraryId) params.set('id', data.itineraryId);
      if (data?.state) params.set('state', data.state);
      break;
    }
    case 'trek-detail': {
      if (data?.itineraryId) params.set('id', data.itineraryId);
      if (data?.state) params.set('state', data.state);
      break;
    }
  }
  const query = params.toString();
  return `/${page}${query ? '?' + query : ''}`;
}

function App() {
  const [currentPage, setCurrentPage] = useState<string>('home');
  const [pageData, setPageData] = useState<any>(null);
  const [transitionKey, setTransitionKey] = useState(0);
  const isPopState = useRef(false);
  const currentPageRef = useRef('home');

  // ── Browser history: restore page from URL on mount (deep linking) ──
  useEffect(() => {
    const { page, data } = parseUrlToPageData();
    setCurrentPage(page);
    setPageData(data);
    currentPageRef.current = page;
    window.history.replaceState({ page, data }, '', window.location.pathname + window.location.search);
  }, []);

  // ── Browser history: listen for back/forward ────────────────────────
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const newPage = e.state?.page || 'home';
      const newData = e.state?.data || null;

      isPopState.current = true;
      setCurrentPage(newPage);
      setPageData(newData);

      // Only animate transition and scroll when navigating to a different page
      // (skip for modal-close back navigations within the same page)
      if (newPage !== currentPageRef.current) {
        setTransitionKey(k => k + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      currentPageRef.current = newPage;
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ── Navigation handler with history push ────────────────────────────
  const handleNavigateTo = useCallback((page: string, data?: any) => {
    setCurrentPage(page);
    setPageData(data || null);
    setTransitionKey(k => k + 1);

    // Only push state for forward navigation, not for popstate events
    if (!isPopState.current) {
      const url = buildUrlFromPageData(page, data);
      window.history.pushState({ page, data: data || null }, '', url);
    }
    isPopState.current = false;
    currentPageRef.current = page;

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ── Render current page ─────────────────────────────────────────────
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
      case 'destinations':
        return (
          <HomePage
            onNavigateTo={handleNavigateTo}
            scrollToDestinations={currentPage === 'destinations'}
          />
        );
      case 'reviews':
        return (
          <ReviewPage
            onNavigateTo={handleNavigateTo}
            onClose={() => handleNavigateTo('home')}
          />
        );
      case 'carousel-demo':
        return <CarouselComparison />;
      case 'destination-page':
        return (
          <DestinationPage
            destination={pageData?.destination || null}
            onNavigateTo={handleNavigateTo}
            onBack={() => handleNavigateTo('home')}
          />
        );
      case 'curated-experiences':
        return (
          <CuratedExperiences
            destination={pageData?.destination || null}
            onNavigateTo={handleNavigateTo}
            onBack={() => handleNavigateTo('destination-page', { destination: pageData?.destination })}
            tourType={pageData?.tourType}
          />
        );
      case 'destinations-tiles':
        return (
          <DestinationsTilesPage
            onBack={() => handleNavigateTo('home')}
            onSelectDestination={(destination) => {
              handleNavigateTo('destination-page', { destination });
            }}
            onNavigateTo={handleNavigateTo}
          />
        );
      case 'detailed-itinerary':
        return (
          <DetailedItineraryPage
            state={pageData?.state || 'ladakh'}
            itineraryId={pageData?.itineraryId || ''}
            onBack={() => {
              // Route back to curated-experiences if user came from there
              if (pageData?.fromCuratedExperiences) {
                handleNavigateTo('curated-experiences', { destination: pageData?.fromDestination });
              } else {
                handleNavigateTo('destination-page', { destination: pageData?.fromDestination });
              }
            }}
            onNavigateTo={handleNavigateTo}
          />
        );
      case 'trek-detail':
        return (
          <TrekDetailPage
            trekId={pageData?.itineraryId || ''}
            state={pageData?.state || undefined}
            onBack={() => {
              if (pageData?.fromHomepage) {
                handleNavigateTo('home');
              } else {
                handleNavigateTo('curated-experiences', { destination: pageData?.fromDestination });
              }
            }}
            onNavigateTo={handleNavigateTo}
          />
        );
      case 'about':
        return <AboutPage onNavigateTo={handleNavigateTo} />;
      case 'contact':
        return <ContactPage onNavigateTo={handleNavigateTo} />;
      case 'flipcard-test':
        return <FlipCardTestPage />;
      default:
        return null;
    }
  };

  return (
    <SessionProvider>
      <div className="App">
        <main className="app-main">
          <div key={transitionKey} className="page-transition">
            {renderPage()}
          </div>
        </main>
        {/* Chat Widget persists across all pages with same session ID */}
        <ChatWidget />
      </div>
    </SessionProvider>
  );
}

export default App;
